import React from "react"
import { useDraggable, useDndContext } from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical } from 'lucide-react'
import defaultImg from "../assets/img/faces/5.jpg"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar, faPencil, faPerson, faPhoneVolume, faTrashAlt, faUser } from "@fortawesome/free-solid-svg-icons"
import { toast } from "sonner"
import { baseURL } from "../constants/constant"
import { maskContactNumber } from "../helpers/commonhelper"

export const KanbanItem = React.memo(
  ({ mainGrant, followupGrant, item, columnId, dragOverlay = false, isUpdating = false, onEdit, onDelete, onOpenLeadDetails, individualBudget, stageColor , highlightId }) => {
    // --------Get drag and drop context
    const { active } = useDndContext()

    // --------Set up draggable functionality for the lead item
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useDraggable({
      id: item.lead_id,
      data: {
        type: "Task",
        task: item,
        columnId,
      },
    })

    // --------Parse and format phone number from JSON string
    function loadPhoneNumber(contact_phone) {
      if (!contact_phone) return "-"

      try {
        const parsed = JSON.parse(contact_phone)

        if (Array.isArray(parsed) && parsed.length > 0) {
          const { country_code, phone } = parsed[0]

          if (country_code && phone) {
            return `+${country_code} ${maskContactNumber(phone)}`
          }
        }
      } catch (e) {
        console.error("Invalid JSON format", e)
      }

      return "-"
    }
    /// ---------for highfliting the latest deal which was converted from lead
    const isHighlight = String(item.lead_id) === String(highlightId)

    // --------Format date string to readable format
    const formatDate = (dateStr) => {
      if (!dateStr) return "Not specified"
      const date = new Date(dateStr)
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    }

    // --------Handle edit action - calls parent component's edit handler
    const handleEdit = (e) => {
      e.stopPropagation()
      onEdit && onEdit(item.lead_id)
    }

    // --------Handle follow up action - calls parent component's follow up handler
    // const handleFollowUp = (e) => {
    //   e.stopPropagation()
    //   onFollowUp && onFollowUp(item.lead_id)
    // }

    // --------Handle delete action - calls parent component's delete handler
    const handleDelete = (e) => {
      e.stopPropagation()
      onDelete && onDelete(item.lead_id)
    }

    // --------FIXED: Handle opening lead details - passes the complete item object with proper ID
    const handleShowDetails = () => {
      if (onOpenLeadDetails) {
        // Ensure we pass the complete item with the correct lead_id
        const leadData = {
          ...item,
          id: item.lead_id, // Ensure both lead_id and id are available
          lead_id: item.lead_id
        }
        onOpenLeadDetails(leadData)
      }
    }

    const totalBudget = Array(item).reduce((sum, item) => {
      try {
        const productService = item.product_services
        const individualBudget = productService.reduce((acc, curr) => parseFloat(acc) + parseFloat(curr.budget), 0)
        return sum + individualBudget;
      } catch (error) {
        return sum;
      }
    }, 0).toLocaleString()

    // // --------Color selection logic
    const colorOptions = ["#ffffff", "#ffe8cd", "#e4e6f9", "#d8f8ff", "#cbffe8", "#f3f3f3", "#ffe0fd", "#ffe0e0"]
    // white, 
    const colorsDiv = ["#004aff61", '#ffc584', "#919aff", '#89eaff', '#74f4bd', '#dfdfdf', '#fca2f6', "#feb9b9",];
    // skin contrast, cyan contrast, light skin, grey contrast, pink contrast, light red contrast , ideal for white,purple
    // // Function to select color from colorsDiv based on stageColor or other criteria
    const getSelectedColor = () => {
      // Option 1: If stageColor is provided and exists in colorOptions, use corresponding colorsDiv color
      if (stageColor) {
        const colorIndex = colorOptions.indexOf(stageColor);
        if (colorIndex !== -1) {
          return colorsDiv[colorIndex];
        }
      }

      //   // // Option 2: Use a default selection logic based on lead_id or other property
      if (item.lead_id) {
        const colorIndex = item.lead_id % colorsDiv.length;
        return colorsDiv[colorIndex];
      }

      //   // Option 3: Fallback to first color
      return colorsDiv[0];
    }

    const selectedColor = getSelectedColor();

    // --------Dynamic styles for drag and drop functionality
    const cardStyle = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging && !dragOverlay ? 0.5 : 1,
      zIndex: dragOverlay ? 1000 : "auto",
      backgroundColor: stageColor || "#ffffff", // Use stageColor if provided, otherwise white
      borderRadius: "10px",
      padding: "12px",
      width: "100%",
      boxShadow: dragOverlay ? "0 4px 12px rgba(0,0,0,0.15)" : "0 2px 6px rgba(0,0,0,0.08)",
      userSelect: "none",
      position: dragOverlay ? "fixed" : "relative",
      border: "1px solid #dddddd",
    }

    // --------Overlay style for updating state - shows diagonal stripes when lead is being updated
    const overlayStyle = {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `repeating-linear-gradient(
        45deg,
        rgba(0, 0, 0, 0.1),
        rgba(0, 0, 0, 0.1) 2px,
        transparent 2px,
        transparent 8px
      )`,
      borderRadius: "8px",
      zIndex: 10,
      pointerEvents: "none",
    }

    return (
      <div ref={setNodeRef} style={cardStyle} {...attributes} className={`pb-0 ${isHighlight ? "kanban-blink" : ""}`}>
        {/* Show updating overlay when item is being updated */}
        {isUpdating && <div style={overlayStyle} />}

        {/* Header section with contact info and avatar */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {/* Contact information section */}
            <div>
              {/* Drag handle icon (hidden by default, shown on hover) */}
              <div
                style={{
                  position: "absolute",
                  marginLeft: "43%",
                  marginTop: "-22px",
                  zIndex: 1111,
                  transform: "rotate(90deg)",
                  display: "none",
                }}
                className="moveIcons"
              >
                <GripVertical size={20} />
              </div>

              {/* Contact name and company display */}
              <div>
                <p
                  className="mb-0 fw-bold"
                  style={{
                    width: "21ch",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    cursor: "pointer",
                    textOverflow: "ellipsis",
                    textTransform: 'capitalize'
                  }}
                  onClick={handleShowDetails}
                >
                  {item.requirement}
                </p>
                <p className="mb-0 fs-13" style={{ color: "grey", cursor: "grab" }} {...listeners}>
                  {item.company_name || "-"}
                </p>
              </div>
            </div>

            {/* Avatar and action menu section */}
            <div className="kanbanCards">
              <div
                className="avatar avatar-md avatar-rounded"
                style={{ cursor: "pointer" }}
                onClick={handleShowDetails}
              >
                <img
                  src={item.contact_image ? `${baseURL}${item.contact_image}` : defaultImg}
                  alt="Contact avatar"
                />
              </div>

              {/* Action menu (appears on hover) */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  color: "#fff",
                  height: "100%",
                  background: "#0099ff", // Use the selected color from colorsDiv
                  border: "1px solid #ededed",
                  borderRadius: 2,
                  boxShadow: "none",
                  zIndex: 1000,
                  paddingTop: "10px",
                  minWidth: "7rem",
                }}
                className="kanbanAction"
              >
                {/* Edit action button */}
                <div
                  onClick={(e) => {
                    if (!mainGrant?.isView) {
                      toast.warning("You are not authorized for this action!")
                      return
                    }
                    handleEdit(e)
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <FontAwesomeIcon icon={faPencil} /> Edit
                </div>

                {/* Follow up action button */}
                {/* <div
                  onClick={(e) => {
                    if (!followupGrant?.isView) {
                      toast.warning("You are not authorized for this action!")
                      return
                    }
                    handleFollowUp(e)
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <FontAwesomeIcon icon={faCalendar} /> Follow Up
                </div> */}

                {/* Delete action button */}
                <div
                  onClick={(e) => {
                    if (!mainGrant?.isDelete) {
                      toast.warning("You are not authorized for this action!")
                      return
                    }
                    handleDelete(e)
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <FontAwesomeIcon icon={faTrashAlt} /> Delete
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Phone number section with drag handle */}
        <div
          className="mb-0 fs-11 d-flex align-items-center gap-2"
          {...listeners}
          style={{
            cursor: "grab",
            paddingBottom: "0.6rem",
            paddingTop: "0.6rem",
          }}
        >
          <span
            style={{
              textWrap: "nowrap",
              color: "black",
              fontWeight: 500,
              letterSpacing: "0.25px"
            }}
          >
            <FontAwesomeIcon icon={faPhoneVolume} style={{ fontSize: "10px" }} />
            {loadPhoneNumber(item.contact_phone)}
          </span>
          <span style={{
            textWrap: "nowrap",
            fontWeight: 500,
          }}>     <FontAwesomeIcon icon={faUser} style={{ fontSize: "10px", color: "grey", width: 10 }} /> {item.contact_full_name}</span>
        </div>
        {/* Divider line */}
        <hr
          style={{
            paddingTop: "0.6rem",
            margin: 0,
            cursor: "grab",
          }}
          {...listeners}
        />

        {/* Footer section with follow-up date and budget */}
        <div
          style={{
            fontSize: "0.71rem",
            color: "#333",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "grab",
            paddingBottom: "10px",
          }}
          {...listeners}
        >
          {/* Follow-up date and time display */}
          <span>
            <FontAwesomeIcon
              icon={faCalendar}
              style={{
                fontSize: "10px",
                color: "#536485",
                marginRight: "3px",
              }}
            />
            {formatDate(item.followup_date)} at {item.followup_time}
          </span>

          {/* Budget badge display */}
          <span className="d-flex">
            {(item.lead_status === "lost" || item.lead_status === "unqualified") && (
              <span
                className={`badge ${item.lead_status === "lost" ? "bg-danger" : "bg-warning"
                  }`}
                style={{ fontSize: "10px", marginRight: "0.5rem" }}
              >
                {item.lead_status}
              </span>
            )}
            <span
              className="badge"
              style={{
                backgroundColor: selectedColor, // Use the selected color from colorsDiv for budget badge
                fontWeight: 500,
                borderRadius: "9px",
                fontSize: "10px",
                color: 'black'
              }}
            >
              ₹ {totalBudget || 0}
            </span>
          </span>
        </div>
      </div>
    )
  },
)